import type {
  MasterToolSearchFilters,
  SuperShowcaseActionType,
  SuperShowcaseHotspot,
  SuperShowcaseLinkConfig,
  SuperShowcaseProjectResearchConfig,
} from '../../services/api';

export interface ShowcaseActionMeta {
  type: SuperShowcaseActionType;
  label: string;
  /** One-liner shown in the admin picker and on the agent-facing tooltip. */
  description: string;
  /** Default outline colour when the admin doesn't pick one. */
  color: string;
}

/**
 * The Super Picker functions a showcase hotspot can open — plus LINK, which
 * leaves the app for a URL. Adding a new action means adding it here, in the
 * backend's ACTION_TYPES, and in the app's `handleSuperShowcaseHotspot`
 * dispatcher.
 */
export const SHOWCASE_ACTIONS: ShowcaseActionMeta[] = [
  {
    type: 'PRICE_PROMO',
    label: 'Price Promo Units',
    description: 'Opens the Price Promo Units dialog',
    color: '#E5484D',
  },
  {
    type: 'THUMBSUP',
    label: 'Thumbsup Units',
    description: 'Opens the Thumbsup Units dialog',
    color: '#30A46C',
  },
  {
    type: 'PROJECT_RESEARCH',
    label: 'Project Research',
    description: 'Runs a pre-filled Project Research search',
    color: '#F5A524',
  },
  {
    type: 'ANTICIPATE_BLINKER',
    label: 'Anticipate Blinker Unit',
    description: 'Opens P2510001 (3BR, else 2BR) or falls back to a random site plan as Anticipate Blinker',
    color: '#1B5E20',
  },
  {
    type: 'SUPER_RATIO',
    label: 'Super Ratio',
    description: 'Opens a random site plan on the Super Ratio tab',
    color: '#0F766E',
  },
  {
    type: 'REGION_MARKET_DATA',
    label: 'Regional Market Comparison',
    description: 'Shows new-launch PSF/PPR and same-region GLS land costs',
    color: '#F5A524',
  },
  {
    type: 'LINK',
    label: 'Hyperlink',
    description: 'Opens a link in a new tab',
    color: '#0091FF',
  },
];

/** Keeps a pasted URL inside the ActionConfig column's budget. */
export const MAX_LINK_URL_LENGTH = 2000;

export const SHOWCASE_ACCENT_PALETTE = [
  '#E5484D',
  '#F5A524',
  '#30A46C',
  '#0091FF',
  '#8E4EC6',
  '#FFFFFF',
];

export function getShowcaseActionMeta(type: SuperShowcaseActionType | string): ShowcaseActionMeta {
  return SHOWCASE_ACTIONS.find((action) => action.type === type) ?? SHOWCASE_ACTIONS[0];
}

export function getHotspotColor(hotspot: Pick<SuperShowcaseHotspot, 'actionType' | 'accentColor'>): string {
  return hotspot.accentColor || getShowcaseActionMeta(hotspot.actionType).color;
}

/** The saved search on a PROJECT_RESEARCH hotspot, or null for any other action. */
export function getProjectResearchConfig(
  hotspot: Pick<SuperShowcaseHotspot, 'actionType' | 'actionConfig'>,
): SuperShowcaseProjectResearchConfig | null {
  if (hotspot.actionType !== 'PROJECT_RESEARCH') return null;
  return (hotspot.actionConfig as SuperShowcaseProjectResearchConfig | null) ?? null;
}

/** The saved URL on a LINK hotspot, or null when it is another action or unset. */
export function getLinkUrl(
  hotspot: Pick<SuperShowcaseHotspot, 'actionType' | 'actionConfig'>,
): string | null {
  if (hotspot.actionType !== 'LINK') return null;
  const config = hotspot.actionConfig as SuperShowcaseLinkConfig | null;
  return normalizeShowcaseLinkUrl(config?.url);
}

/**
 * Accept the URLs an admin actually types (`huttons.com/x` as well as
 * `https://huttons.com/x`) and reject everything that isn't a real web page —
 * `javascript:` and `data:` above all, since the viewer hands this straight to
 * `window.open`. Returns the absolute URL, or null when it can't be one.
 */
export function normalizeShowcaseLinkUrl(raw: string | null | undefined): string | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed || trimmed.length > MAX_LINK_URL_LENGTH) return null;
  // A bare host like "huttons.com/promo" is a link an admin means as https.
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  if (!parsed.hostname) return null;
  return parsed.toString();
}

function formatMoney(value: string | undefined): string | null {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return null;
  return `$${num.toLocaleString('en-SG', { maximumFractionDigits: 0 })}`;
}

function formatRange(
  label: string,
  min: string | undefined,
  max: string | undefined,
  format: (value: string | undefined) => string | null = (v) => ((v ?? '').trim() || null),
  suffix = '',
): string | null {
  const from = format(min);
  const to = format(max);
  if (!from && !to) return null;
  if (from && to) return `${label} ${from}${suffix} – ${to}${suffix}`;
  if (from) return `${label} from ${from}${suffix}`;
  return `${label} up to ${to}${suffix}`;
}

/**
 * Human-readable chips for a saved Project Research search. Used in the admin
 * hotspot list and on the agent-facing hotspot tooltip, so an agent knows what
 * a click is about to run.
 */
export function summarizeProjectResearchFilters(filters: MasterToolSearchFilters | undefined): string[] {
  if (!filters) return [];
  const parts: string[] = [];

  // `priceMin: '1'` is the app-wide default (exclude $0 units) — not worth showing.
  const priceMin = filters.priceMin === '1' ? undefined : filters.priceMin;
  const price = formatRange('Price', priceMin, filters.priceMax, formatMoney);
  if (price) parts.push(price);

  const psf = formatRange('PSF', filters.psfMin, filters.psfMax, formatMoney);
  if (psf) parts.push(psf);

  const size = formatRange('Size', filters.sizeMin, filters.sizeMax, undefined, ' sqft');
  if (size) parts.push(size);

  const bedroom = formatRange('Bedroom', filters.bedroomMin, filters.bedroomMax);
  if (bedroom) parts.push(bedroom);

  const floor = formatRange('Floor', filters.floorMin, filters.floorMax);
  if (floor) parts.push(floor);

  const bath = formatRange('Bath', filters.bathMin, filters.bathMax);
  if (bath) parts.push(bath);

  const topYear = formatRange('TOP', filters.topYearMin, filters.topYearMax);
  if (topYear) parts.push(topYear);

  if (filters.districts?.length) parts.push(filters.districts.map((d) => `D${d}`).join(', '));
  if (filters.regions?.length) parts.push(filters.regions.join(', '));
  if (filters.tenure?.length) {
    parts.push(
      filters.tenure
        .map((t) => (t === '99' ? '99-year' : t === 'freehold' ? 'Freehold' : '999-year'))
        .join(', '),
    );
  }
  if (filters.projectCategories?.length) {
    parts.push(`${filters.projectCategories.length} categor${filters.projectCategories.length === 1 ? 'y' : 'ies'}`);
  }
  if (filters.phOnly) parts.push('Penthouse only');
  if (filters.dualKeyOnly) parts.push('Dual key only');
  if (filters.locationLabel && filters.radiusKm) {
    parts.push(`Within ${filters.radiusKm}km of ${filters.locationLabel}`);
  }

  return parts;
}

/** One-line description of what a hotspot does, for lists and tooltips. */
export function describeHotspotAction(hotspot: Pick<SuperShowcaseHotspot, 'actionType' | 'actionConfig'>): string {
  const meta = getShowcaseActionMeta(hotspot.actionType);
  if (hotspot.actionType === 'LINK') {
    // getLinkUrl only ever returns a URL that parsed, so the host is safe to read.
    const url = getLinkUrl(hotspot);
    // The host alone is what an agent recognises; the full URL rarely fits.
    return url ? `Opens ${new URL(url).host} in a new tab` : meta.description;
  }
  if (hotspot.actionType !== 'PROJECT_RESEARCH') return meta.description;
  const config = getProjectResearchConfig(hotspot);
  const summary = summarizeProjectResearchFilters(config?.filters);
  const view = config?.viewMode === 'unit' ? 'Units view' : 'Projects view';
  return summary.length > 0 ? `${view} · ${summary.join(' · ')}` : `${view} · all matching projects`;
}
